import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { StatCard } from './costs/plata-kit';

// Insight de mensajes de WhatsApp enviados en el mes, arriba de Atención (20/10/2026).
// Sirve para estimar la factura de Meta con el modelo vigente desde el 01/10/2026:
//  - marketing y utility: se cobra cada template ENTREGADO (utility ya no es gratis dentro de la ventana de 24 h);
//  - servicio (texto libre del agente o escrito a mano dentro de la ventana): 1.000 gratis por mes
//    por número de teléfono, después se cobra a la tarifa de utility.
// Cuenta lo que salió de verdad: saliente, no fallido y con id de Meta (meta_message_id).
// Categoría de cada template: Meta en vivo (wa-template-get {list:true}); si no aparece, utility.
// Tarifas de Argentina en USD (rate card publicada oct/2026): si Meta las cambia, editarlas acá.
export const WA_RATES_USD = { marketing: 0.0618, utility: 0.026, service: 0.026 };
export const WA_FREE_SERVICE = 1000;

type Cat = 'marketing' | 'utility' | 'service';
const usd = (n: number) => 'USD ' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = (n: number) => Math.round(n).toLocaleString('es-AR');

// Inicio del mes en hora de Argentina (UTC-3, sin horario de verano).
function monthStartAR(d = new Date()): Date {
    const ar = new Date(d.getTime() - 3 * 3600000);
    return new Date(Date.UTC(ar.getUTCFullYear(), ar.getUTCMonth(), 1, 3));
}

export interface WaMonthUsage {
    counts: Record<Cat, number>;
    byTpl: [string, number, Cat][];
    costUSD: number;
    /** El modelo nuevo (servicio cobrado, utility siempre cobrada) rige desde 2026-10. */
    newModel: boolean;
}

// Categorías de Meta: una consulta por sesión.
let catsPromise: Promise<Map<string, string>> | null = null;
function loadCats(): Promise<Map<string, string>> {
    if (!catsPromise) catsPromise = Promise.resolve(supabase.functions.invoke('wa-template-get', { body: { list: true } })).then(({ data }) => {
        const m = new Map<string, string>();
        (data?.templates || []).forEach((t: any) => m.set(t.name, String(t.category || '').toUpperCase()));
        if (!m.size) catsPromise = null; // falló: reintentar la próxima vez
        return m;
    });
    return catsPromise;
}

/** Costo en USD con las reglas de Meta del mes. Antes de 2026-10 el texto libre era gratis
 *  (y la utility dentro de la ventana también: no se descuenta → leve sobreestimación). */
export function waCostUSD(c: Record<Cat, number>, newModel = true): number {
    return c.marketing * WA_RATES_USD.marketing + c.utility * WA_RATES_USD.utility
        + (newModel ? Math.max(0, c.service - WA_FREE_SERVICE) * WA_RATES_USD.service : 0);
}

/** Mensajes enviados (de verdad) en el mes `ym` = 'YYYY-MM' (hora AR). */
export async function fetchWaMonthUsage(ym: string): Promise<WaMonthUsage> {
    const [y, m] = ym.split('-').map(Number);
    const start = new Date(Date.UTC(y, m - 1, 1, 3));
    const next = new Date(Date.UTC(y, m, 1, 3));
    const [cats, rows] = await Promise.all([
        loadCats(),
        (async () => {
            const out: { template_name: string | null }[] = [];
            for (let from = 0; ; from += 1000) {
                const { data, error } = await supabase.from('whatsapp_logs').select('template_name')
                    .eq('direction', 'outgoing').not('meta_message_id', 'is', null).is('error_message', null)
                    .or('status.is.null,status.neq.failed')
                    .gte('created_at', start.toISOString()).lt('created_at', next.toISOString())
                    .order('id').range(from, from + 999);
                if (error || !data) break;
                out.push(...data);
                if (data.length < 1000) break;
            }
            return out;
        })(),
    ]);
    const counts: Record<Cat, number> = { marketing: 0, utility: 0, service: 0 };
    const tpl = new Map<string, number>();
    for (const r of rows) {
        if (!r.template_name) { counts.service++; continue; }
        counts[cats.get(r.template_name) === 'MARKETING' ? 'marketing' : 'utility']++;
        tpl.set(r.template_name, (tpl.get(r.template_name) || 0) + 1);
    }
    const byTpl = Array.from(tpl.entries())
        .map(([n, k]) => [n, k, cats.get(n) === 'MARKETING' ? 'marketing' : 'utility'] as [string, number, Cat])
        .sort((a, b) => b[1] - a[1]);
    const newModel = ym >= '2026-10';
    return { counts, byTpl, costUSD: waCostUSD(counts, newModel), newModel };
}

export function WaUsage() {
    const [data, setData] = useState<null | { counts: Record<Cat, number>; byTpl: [string, number, Cat][]; elapsed: number; days: number; month: string }>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            const start = monthStartAR();
            const ar = new Date(start.getTime() + 86400000);
            const ym = `${ar.getUTCFullYear()}-${String(ar.getUTCMonth() + 1).padStart(2, '0')}`;
            const u = await fetchWaMonthUsage(ym);
            if (!alive) return;
            const next = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1, 3));
            const days = Math.round((next.getTime() - start.getTime()) / 86400000);
            const elapsed = Math.max(0.5, Math.min(days, (Date.now() - start.getTime()) / 86400000));
            const month = ar.toLocaleDateString('es-AR', { month: 'long', timeZone: 'UTC' });
            setData({ counts: u.counts, byTpl: u.byTpl, elapsed, days, month });
        })();
        return () => { alive = false; };
    }, []);

    if (!data) return <div className="tw mb-3 h-[104px] animate-pulse rounded-xl bg-[color:var(--border)] opacity-40" />;

    const { counts, elapsed, days, month } = data;
    const proj = (n: number) => n * days / elapsed;
    const cost = (c: Record<Cat, number>) => waCostUSD(c);
    const total = counts.marketing + counts.utility + counts.service;
    const projCounts = { marketing: proj(counts.marketing), utility: proj(counts.utility), service: proj(counts.service) };
    const svcPct = Math.min(100, counts.service / WA_FREE_SERVICE * 100);
    const svcProjOver = projCounts.service > WA_FREE_SERVICE;

    return (
        <div className="tw mb-3 grid gap-2">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
                <StatCard label={`Enviados en ${month}`} value={int(total)} context={`~${int(proj(total))} a fin de mes`} />
                <StatCard label="Marketing" value={int(counts.marketing)} context={`${usd(counts.marketing * WA_RATES_USD.marketing)} · review y carritos`} />
                <StatCard label="Utility" value={int(counts.utility)} context={`${usd(counts.utility * WA_RATES_USD.utility)} · confirmación, envíos, pagos`} />
                <StatCard label="Servicio (texto libre)" value={`${int(counts.service)} / ${int(WA_FREE_SERVICE)}`}
                    tone={counts.service > WA_FREE_SERVICE ? 'danger' : svcProjOver ? 'warning' : undefined}
                    context={counts.service > WA_FREE_SERVICE
                        ? `${int(counts.service - WA_FREE_SERVICE)} ya se cobran`
                        : svcProjOver ? `al ritmo actual se pasa (~${int(projCounts.service)})` : `gratis · quedan ${int(WA_FREE_SERVICE - counts.service)}`}>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--border)]">
                        <div className={`h-full rounded-full ${svcProjOver ? 'bg-[color:var(--warning)]' : 'bg-[color:var(--success)]'}`} style={{ width: `${svcPct}%` }} />
                    </div>
                </StatCard>
                <StatCard label="Costo estimado" value={usd(cost(counts))} context={`proyectado ${usd(cost(projCounts))} en ${month}`}
                    badge={<button type="button" onClick={() => setOpen(v => !v)} className="cursor-pointer text-[length:var(--text-xs)] text-soft underline-offset-2 hover:underline">{open ? 'ocultar' : 'detalle'}</button>} />
            </div>
            {open && (
                <div className="rounded-xl border border-line bg-[var(--card)] px-4 py-3 text-[length:var(--text-sm)]">
                    <div className="grid gap-1 sm:grid-cols-2 sm:gap-x-8">
                        {data.byTpl.map(([n, k, c]) => (
                            <div key={n} className="flex justify-between gap-3">
                                <span className="truncate text-ink">{n} <span className="text-faint">· {c}</span></span>
                                <span className="tabular-nums text-soft">{int(k)}</span>
                            </div>
                        ))}
                        <div className="flex justify-between gap-3">
                            <span className="truncate text-ink">texto libre (agente + a mano) <span className="text-faint">· servicio</span></span>
                            <span className="tabular-nums text-soft">{int(counts.service)}</span>
                        </div>
                    </div>
                    <p className="mt-2 text-[length:var(--text-xs)] text-faint">
                        Modelo de Meta desde 01/10/2026: cada template entregado se cobra (marketing USD {WA_RATES_USD.marketing} · utility USD {WA_RATES_USD.utility}, Argentina);
                        el texto libre tiene {int(WA_FREE_SERVICE)} gratis por mes y después USD {WA_RATES_USD.service}. Estimación: la factura real está en WhatsApp Manager → Estadísticas.
                    </p>
                </div>
            )}
        </div>
    );
}
